import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, startWith, switchMap, take } from 'rxjs';

import { MediaQueryService } from '@fcom/common/services';
import { ConfigService, NativeBridgeService } from '@fcom/core/services';
import { cartData } from '@fcom/booking/store/selectors';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';
import { Breakpoint, MultivariateTestId, TestVariant } from '@fcom/common/interfaces';
import { selectActiveTab } from '@fcom/booking-widget/store';
import { WidgetTab } from '@fcom/booking-widget/interfaces';

import { isAwardBooking } from '../utils/common-booking.utils';

interface ShoppingCartBoundOptions {
  isDirect?: boolean;
  boundIndex?: number;
  isMulticity?: boolean;
}

const MOBILE_VIEW_BREAKPOINT: Breakpoint = 'tablet_down';

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartService {
  configService = inject(ConfigService);
  multivariateTestService = inject(MultivariateTestService);
  nativeBridgeService = inject(NativeBridgeService);
  store$ = inject(Store);
  private readonly mediaQueryService = inject(MediaQueryService);

  readonly isEnabled: Signal<boolean> = signal<boolean>(false);
  readonly isOpen = signal<boolean>(false);
  readonly showDesktopCart = computed(
    () => this.isEnabled() && !this.mediaQueryService.isBreakPoint(MOBILE_VIEW_BREAKPOINT)()
  );
  readonly showModalCart = computed(
    () => this.isEnabled() && this.mediaQueryService.isBreakPoint(MOBILE_VIEW_BREAKPOINT)()
  );
  readonly isAllowedToShowCtaInModal = signal(true);

  private readonly boundSelections = new Map<number, boolean>();

  private readonly isTestVariantB: WritableSignal<boolean> = signal<boolean>(false);
  private readonly isAward: Signal<boolean> = signal<boolean>(false);
  private readonly isMulticity: WritableSignal<boolean> = signal<boolean>(false);
  private readonly allBoundsDirect: WritableSignal<boolean> = signal<boolean>(true);

  constructor() {
    if (!this.configService.cfg.enableShoppingCart) {
      return;
    }

    this.isAward = toSignal(this.isAward$(), { requireSync: true });

    this.store$
      .pipe(
        cartData(),
        filter(Boolean),
        map(({ bounds }) => bounds.every((bound) => bound.flights === 1)),
        switchMap((direct) =>
          this.multivariateTestService
            .getTestVariant(MultivariateTestId.SHOPPING_CART_THREE, true, false, { label: `direct flight: ${direct}` })
            .pipe(
              take(1),
              map(({ variant }) => [direct, variant === TestVariant.B] as [boolean, boolean])
            )
        ),
        takeUntilDestroyed()
      )
      .subscribe(([direct, variant]) => {
        this.isTestVariantB.set(variant);
        this.allBoundsDirect.set(direct);
      });

    this.isEnabled = computed(
      () =>
        this.isTestVariantB() &&
        this.allBoundsDirect() &&
        !this.isAward() &&
        !this.isMulticity() &&
        !this.nativeBridgeService.isInsideNativeWebView
    );
  }

  updateBoundSelection({ boundIndex, isDirect, isMulticity }: ShoppingCartBoundOptions): void {
    if (!this.configService.cfg.enableShoppingCart) {
      return;
    }

    this.isMulticity.set(isMulticity);

    if (isMulticity) {
      return;
    }

    if (boundIndex === 0) {
      this.boundSelections.clear();
    }

    this.boundSelections.set(boundIndex, isDirect);
    this.allBoundsDirect.set([...this.boundSelections.values()].every((direct) => direct));
  }

  setVisible(visible: boolean): void {
    this.isOpen.set(visible);
  }

  private isAward$(): Observable<boolean> {
    return combineLatest([
      this.store$.select(selectActiveTab).pipe(startWith(null)),
      this.store$.pipe(cartData(), startWith(null)),
    ]).pipe(map(([tab, cart]) => tab === WidgetTab.AWARD || (cart ? isAwardBooking(cart.prices.unpaid) : false)));
  }
}
